import { useEffect } from 'react'
import { flushSync } from 'react-dom'
import type { NavigateFunction } from 'react-router-dom'
import { splitLocale } from '@/lib/seo'
import { SECTION_PATHS } from '@/lib/sections'

/**
 * Smooth page changes inside the app. Moving between the one-page shell
 * (/, /services, …) and a separate page (/archive, /privacy, /imprint,
 * /demo/<project>) cross-fades the content while the header stays put,
 * using the View Transitions API. Moving between shell sections keeps the
 * shell's own smooth scroll, and browsers without the API (or visitors who
 * prefer reduced motion) navigate exactly as before.
 */

const SHELL = new Set(Object.values(SECTION_PATHS))
const PAGES = new Set(['/archive', '/imprint', '/privacy', '/nisg-2026'])

function routed(pathname: string): { group: string; path: string } | null {
  const { path } = splitLocale(pathname)
  if (SHELL.has(path)) return { group: 'shell', path }
  if (PAGES.has(path) || /^\/demo\/[a-z0-9-]+$/.test(path)) return { group: path, path }
  return null // static files (studies, demo apps) and anything else: leave to the browser
}

type ViewTransitionDoc = Document & { startViewTransition?: (update: () => void) => unknown }

function canAnimate(): boolean {
  return typeof document !== 'undefined' && !!(document as ViewTransitionDoc).startViewTransition &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Navigate to `to` (a site path), cross-fading when it changes page. */
export function transitionTo(navigate: NavigateFunction, to: string) {
  const from = routed(window.location.pathname)
  const target = routed(new URL(to, window.location.origin).pathname)
  const changesPage = !!from && !!target && from.group !== target.group
  if (!changesPage || !canAnimate()) {
    navigate(to)
    return
  }
  ;(document as ViewTransitionDoc).startViewTransition!(() => {
    flushSync(() => navigate(to))
    // separate pages start at the top; the shell scrolls to its section itself
    if (target!.group !== 'shell') window.scrollTo(0, 0)
  })
}

/** Routes plain <Link> clicks (footer, in-text links) through transitionTo. */
export function useLinkTransitions(navigate: NavigateFunction) {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a')
      if (!a || a.target || a.hasAttribute('download') || a.dataset.noTransition !== undefined) return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin) return
      const from = routed(window.location.pathname), target = routed(url.pathname)
      if (!from || !target || from.group === target.group || !canAnimate()) return
      e.preventDefault()
      transitionTo(navigate, url.pathname + url.search + url.hash)
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [navigate])
}
